import { Alert, Box, Button, Checkbox, FormControlLabel, Grid, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import NotesIcon from '@mui/icons-material/Notes';
import ArrowDropDownCircleOutlinedIcon from '@mui/icons-material/ArrowDropDownCircleOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import {
  getFormSettings,
  updateFormSetting,
  deleteFormSetting,
  EventFormSetting,
  EventFormFieldType,
  fieldTypeLabels,
} from 'src/shared/services/eventFormSettingsService';
import { EventFormSettingEditModal } from './eventFormSettingEditModal';

type EventDetailTabFormSettingsProps = {
  eventId: number;
  hasParticipants: boolean;
  showAllergy: boolean;
  onToggleAllergy: (enabled: boolean) => void;
};

const MAX_FIELDS = 10;

// Parsa pipe-delimiterade dropdown-alternativ
const parseDropdownOptions = (extra: string): { placeholder: string; options: { label: string; value: string }[] } => {
  if (!extra || !extra.startsWith('|')) return { placeholder: '', options: [] };
  const parts = extra.split('|').filter(Boolean);
  if (parts.length === 0) return { placeholder: '', options: [] };

  const placeholder = parts[0];
  const options: { label: string; value: string }[] = [];
  for (let i = 1; i < parts.length; i += 2) {
    options.push({ label: parts[i], value: parts[i + 1] || parts[i] });
  }
  return { placeholder, options };
};

export const EventDetailTabFormSettings = ({ eventId, hasParticipants, showAllergy, onToggleAllergy }: EventDetailTabFormSettingsProps) => {
  const { t } = useTranslation(['event', 'common']);
  const isMounted = useIsMounted();

  const [fields, setFields] = useState<EventFormSetting[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<EventFormSetting | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<number>(0);

  const fetchFields = useCallback(async () => {
    try {
      const res = await getFormSettings(eventId);
      if (isMounted()) setFields(res.data);
    } catch (e) {
      ConsoleHelper.log(e);
    }
  }, [eventId, isMounted]);

  useEffect(() => {
    fetchFields();
  }, [fetchFields]);

  const handleAdd = () => {
    setEditingField(null);
    setEditModalOpen(true);
  };

  const handleEdit = (field: EventFormSetting) => {
    setEditingField(field);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setFieldToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = async (result?: { id: string }) => {
    setDeleteDialogOpen(false);
    if (!result) return;

    try {
      await deleteFormSetting(eventId, Number(result.id));
      toast.success(t('event:formSettings.deleteSuccess') as string);
      fetchFields();
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('event:formSettings.deleteError') as string);
    }
  };

  const handleModalSave = () => {
    setEditModalOpen(false);
    setEditingField(null);
    fetchFields();
  };

  const handleToggleShow = async (field: EventFormSetting) => {
    try {
      await updateFormSetting(eventId, field.id, { ...field, show: !field.show });
      fetchFields();
    } catch (e) {
      ConsoleHelper.log(e);
    }
  };

  const usedFieldNames = fields.map((f) => f.fieldName);
  const sortedFields = [...fields].sort((a, b) => a.row - b.row || a.id - b.id);
  const visibleFields = sortedFields.filter((f) => f.show);

  const defaultPreviewFields: EventFormSetting[] = [
    {
      id: -1,
      eventID: eventId,
      fieldName: 'firstName',
      heading: 'Förnamn',
      show: true,
      required: true,
      locked: false,
      type: EventFormFieldType.Text,
      extra: '',
      validator: '',
      errorMessage: '',
      fieldSize: 1,
      row: 0,
      col: 0,
    },
    {
      id: -2,
      eventID: eventId,
      fieldName: 'lastName',
      heading: 'Efternamn',
      show: true,
      required: true,
      locked: false,
      type: EventFormFieldType.Text,
      extra: '',
      validator: '',
      errorMessage: '',
      fieldSize: 1,
      row: 1,
      col: 0,
    },
    {
      id: -3,
      eventID: eventId,
      fieldName: 'email',
      heading: 'E-post',
      show: true,
      required: true,
      locked: false,
      type: EventFormFieldType.Text,
      extra: '',
      validator: '',
      errorMessage: '',
      fieldSize: 1,
      row: 2,
      col: 0,
    },
    {
      id: -4,
      eventID: eventId,
      fieldName: 'phone',
      heading: 'Telefon',
      show: true,
      required: true,
      locked: false,
      type: EventFormFieldType.Text,
      extra: '',
      validator: '',
      errorMessage: '',
      fieldSize: 1,
      row: 3,
      col: 0,
    },
    {
      id: -5,
      eventID: eventId,
      fieldName: 'allergy',
      heading: 'Ev. allergi',
      show: showAllergy,
      required: false,
      locked: false,
      type: EventFormFieldType.Text,
      extra: '',
      validator: '',
      errorMessage: '',
      fieldSize: 1,
      row: 4,
      col: 0,
    },
  ];

  const handleMove = async (field: EventFormSetting, direction: 'up' | 'down') => {
    // Hela listan (default + custom) i nuvarande ordning
    const allSorted = [...defaultPreviewFields, ...sortedFields].sort((a, b) => a.row - b.row || a.id - b.id);
    const currentIndex = allSorted.findIndex((f) => f.id === field.id);

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= allSorted.length) return;

    const targetField = allSorted[targetIndex];

    // Sätt row till samma som målfältet — sortering på id gör att custom (positivt id) hamnar efter default (negativt id)
    const newRow = targetField.row;

    // Om vi byter mellan två custom-fält med olika row, byt deras row-värden
    if (targetField.id > 0 && targetField.row !== field.row) {
      try {
        await Promise.all([
          updateFormSetting(eventId, field.id, { ...field, row: targetField.row }),
          updateFormSetting(eventId, targetField.id, { ...targetField, row: field.row }),
        ]);
        fetchFields();
      } catch (e) {
        ConsoleHelper.log(e);
      }
      return;
    }

    // Flytta förbi ett defaultfält eller custom med samma row
    let finalRow = newRow;
    // Om vi har samma row som målet och rör oss uppåt, gå ett steg till
    if (field.row === newRow && direction === 'up') {
      finalRow = newRow - 1;
    }
    // Om vi har samma row som målet och rör oss nedåt, gå ett steg till
    if (field.row === newRow && direction === 'down') {
      finalRow = newRow + 1;
    }

    try {
      await updateFormSetting(eventId, field.id, { ...field, row: finalRow });
      fetchFields();
    } catch (e) {
      ConsoleHelper.log(e);
    }
  };

  const handleMoveUp = (field: EventFormSetting) => handleMove(field, 'up');
  const handleMoveDown = (field: EventFormSetting) => handleMove(field, 'down');

  const typeIcons: Record<number, JSX.Element> = {
    [EventFormFieldType.Text]: <TextFieldsIcon fontSize='small' />,
    [EventFormFieldType.Multiline]: <NotesIcon fontSize='small' />,
    [EventFormFieldType.Dropdown]: <ArrowDropDownCircleOutlinedIcon fontSize='small' />,
    [EventFormFieldType.Checkbox]: <CheckBoxOutlinedIcon fontSize='small' />,
    [EventFormFieldType.Label]: <LabelOutlinedIcon fontSize='small' />,
  };

  const getTypeIcon = (type: number) => {
    const label = fieldTypeLabels[type] ? t(fieldTypeLabels[type]) : String(type);
    const icon = typeIcons[type] || <TextFieldsIcon fontSize='small' />;
    return (
      <Tooltip title={label} arrow>
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>{icon}</Box>
      </Tooltip>
    );
  };

  const allPreviewFields = [...defaultPreviewFields, ...visibleFields].filter((f) => f.show).sort((a, b) => a.row - b.row || a.id - b.id);

  // Rendera ett preview-fält — efterliknar FS.Forms formulärstil
  const renderPreviewField = (field: EventFormSetting) => {
    const isRequired = field.required;
    const label = field.heading + (isRequired ? ' *' : '');

    switch (field.type) {
      case EventFormFieldType.Dropdown: {
        const parsed = parseDropdownOptions(field.extra);
        return (
          <TextField
            select
            size='small'
            fullWidth
            label={label}
            value='__placeholder__'
            sx={{ bgcolor: '#fff', mb: 1.5 }}
            SelectProps={{
              renderValue: () => parsed.placeholder || field.heading,
            }}
          >
            <MenuItem value='__placeholder__'>{parsed.placeholder}</MenuItem>
            {parsed.options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        );
      }
      case EventFormFieldType.Multiline:
        return (
          <TextField size='small' fullWidth multiline rows={3} label={label} InputProps={{ readOnly: true }} sx={{ bgcolor: '#fff', mb: 1.5 }} />
        );
      case EventFormFieldType.Checkbox:
        return <FormControlLabel control={<Checkbox size='small' />} label={<Typography variant='body2'>{label}</Typography>} sx={{ mb: 1.5 }} />;
      case EventFormFieldType.Label:
        return (
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            {field.heading}
          </Typography>
        );
      default:
        return <TextField size='small' fullWidth label={label} InputProps={{ readOnly: true }} sx={{ bgcolor: '#fff', mb: 1.5 }} />;
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Vänster: fältlista */}
      <Grid item xs={12} md={7}>
        <Typography variant='subtitle1' fontWeight='bold'>
          {t('event:formSettings.title')}
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          {t('event:formSettings.description')}
        </Typography>
        {hasParticipants && (
          <Alert severity='warning' sx={{ mb: 1 }}>
            {t('event:formSettings.hasParticipantsWarning')}
          </Alert>
        )}

        {/* Alla fält i samma ordning som formuläret */}
        {[...defaultPreviewFields, ...sortedFields]
          .sort((a, b) => a.row - b.row || a.id - b.id)
          .map((field) => {
            const isDefault = field.id < 0;

            return (
              <Box
                key={field.id}
                display='flex'
                gap={1}
                mb={0.5}
                px={1}
                py={0.5}
                alignItems='center'
                flexWrap='wrap'
                sx={{
                  bgcolor: isDefault ? 'grey.50' : 'primary.50',
                  border: isDefault ? 0 : 1,
                  borderColor: 'primary.200',
                  borderRadius: 1,
                  opacity: field.show ? 1 : 0.5,
                }}
              >
                {getTypeIcon(field.type)}
                <Typography variant='body2' sx={{ flex: 1 }}>
                  {field.heading}
                </Typography>
                {field.required && (
                  <Typography variant='caption' color='text.secondary'>
                    {t('event:formSettings.required')}
                  </Typography>
                )}
                {field.id === -5 && (
                  <IconButton size='small' onClick={() => onToggleAllergy(!showAllergy)}>
                    {showAllergy ? <VisibilityIcon fontSize='small' /> : <VisibilityOffIcon fontSize='small' color='disabled' />}
                  </IconButton>
                )}
                {!isDefault && (
                  <>
                    <IconButton size='small' onClick={() => handleToggleShow(field)}>
                      {field.show ? <VisibilityIcon fontSize='small' /> : <VisibilityOffIcon fontSize='small' color='disabled' />}
                    </IconButton>
                    <IconButton sx={{ mr: -1 }} onClick={() => handleMoveUp(field)}>
                      <ArrowDropUp />
                    </IconButton>
                    <IconButton onClick={() => handleMoveDown(field)}>
                      <ArrowDropDown />
                    </IconButton>
                    <IconButton size='small' onClick={() => handleEdit(field)}>
                      <EditOutlinedIcon fontSize='small' />
                    </IconButton>
                    <IconButton size='small' color='error' onClick={() => handleDeleteClick(field.id)} disabled={hasParticipants}>
                      <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                  </>
                )}
              </Box>
            );
          })}

        <Box sx={{ mt: 1 }}>
          <Button variant='contained' size='small' onClick={handleAdd} disabled={fields.length >= MAX_FIELDS}>
            {fields.length >= MAX_FIELDS ? t('event:formSettings.maxFieldsReached') : t('event:formSettings.addField')}
          </Button>
        </Box>
      </Grid>

      {/* Höger: preview */}
      <Grid item xs={12} md={5}>
        <Typography variant='subtitle1' fontWeight='bold' sx={{ mb: 1 }}>
          {t('event:formSettings.preview')}
        </Typography>
        <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2 }}>
          {allPreviewFields.map((field) => (
            <Box key={field.id}>{renderPreviewField(field)}</Box>
          ))}
        </Box>
      </Grid>

      {editModalOpen && (
        <EventFormSettingEditModal
          open={editModalOpen}
          eventId={eventId}
          field={editingField}
          usedFieldNames={usedFieldNames}
          nextRow={fields.length > 0 ? Math.max(...fields.map((f) => f.row)) + 1 : 5}
          onSave={handleModalSave}
          onClose={() => {
            setEditModalOpen(false);
            setEditingField(null);
          }}
        />
      )}

      {deleteDialogOpen && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          title={t('event:formSettings.deleteField')}
          modaltext={t('event:formSettings.deleteConfirm')}
          value={fieldToDelete.toString()}
          onClose={handleDeleteClose}
          confirmButtonColor='error'
        />
      )}
    </Grid>
  );
};
